import { isPlatformBrowser, DecimalPipe } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, Input, NgZone, OnChanges, OnDestroy, Output, PLATFORM_ID, SimpleChanges, ViewChild } from '@angular/core';
import OLMap from 'ol/Map';
import View from 'ol/View';
import Feature, { FeatureLike } from 'ol/Feature';
import GeoJSON from 'ol/format/GeoJSON';
import Geometry from 'ol/geom/Geometry';
import Point from 'ol/geom/Point';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { fromLonLat, toLonLat } from 'ol/proj';
import { getDistance } from 'ol/sphere';
import { Circle as CircleStyle, Fill, Stroke, Style, Text } from 'ol/style';
import { WORLD_COUNTRIES } from './world-countries.data';

export interface MapLocation { Latitude: number; Longitude: number; }
type MapRegion = Feature<Geometry>;
type MapLevel = 'country' | 'province' | 'district';

@Component({ selector: 'app-location-map', standalone: true, imports: [DecimalPipe], templateUrl: './location-map.component.html', styleUrls: ['./location-map.component.scss'], changeDetection: ChangeDetectionStrategy.OnPush })
export class LocationMapComponent implements AfterViewInit, OnChanges, OnDestroy {
    @ViewChild('mapElement', { static: true }) private mapElement!: ElementRef<HTMLDivElement>;
    @Input() latitude: number | null = null;
    @Input() longitude: number | null = null;
    @Input() height = 440;
    @Input() disabled = false;
    @Input() mapDataUrl = 'assets/maps';
    @Output() readonly locationSelected = new EventEmitter<MapLocation>();

    selectedLocation: MapLocation | null = null;
    private map?: OLMap;
    private countriesSource?: VectorSource<MapRegion>;
    private provincesSource = new VectorSource<MapRegion>({ wrapX: false });
    private districtsSource = new VectorSource<MapRegion>({ wrapX: false });
    private provinceLayer?: VectorLayer<VectorSource<MapRegion>>;
    private districtLayer?: VectorLayer<VectorSource<MapRegion>>;
    private hoveredRegion: MapRegion | null = null;
    private selectedRegion: MapRegion | null = null;
    private activeLevel: MapLevel = 'country';
    private readonly hoverSource = new VectorSource<MapRegion>({ wrapX: false });
    private readonly selectedSource = new VectorSource<MapRegion>({ wrapX: false });
    private readonly markerSource = new VectorSource<Feature<Point>>({ wrapX: false });
    private provinceLoading?: Promise<void>;
    private districtLoading?: Promise<void>;
    private readonly abortController = new AbortController();
    private resizeObserver?: ResizeObserver;
    private destroyed = false;

    private readonly normalStyle = new Style({ fill: new Fill({ color: '#ffffff' }), stroke: new Stroke({ color: '#9aafc2', width: 1 }) });
    private readonly provinceStyle = new Style({ stroke: new Stroke({ color: '#879db2', width: 1.2 }) });
    private readonly districtStyle = new Style({ stroke: new Stroke({ color: '#b1becb', width: 0.85 }) });
    private readonly hoverStyles = [
        new Style({ stroke: new Stroke({ color: 'rgba(39, 135, 203, 0.35)', width: 10 }) }),
        new Style({ fill: new Fill({ color: 'rgba(39, 135, 203, 0.18)' }), stroke: new Stroke({ color: '#2787cb', width: 2.5 }) })
    ];
    private readonly selectedStyles = [
        new Style({ stroke: new Stroke({ color: 'rgba(199, 50, 39, 0.38)', width: 12 }) }),
        new Style({ fill: new Fill({ color: 'rgba(199, 50, 39, 0.18)' }), stroke: new Stroke({ color: '#c73227', width: 3 }) })
    ];
    private readonly onMouseLeave = (): void => { this.hoveredRegion = null; this.hoverSource.clear(); };

    constructor(@Inject(PLATFORM_ID) private readonly platformId: object, private readonly zone: NgZone, private readonly cdr: ChangeDetectorRef) { }

    ngAfterViewInit(): void { if (isPlatformBrowser(this.platformId)) this.zone.runOutsideAngular(() => this.initializeMap()); }
    ngOnChanges(changes: SimpleChanges): void { if (changes['latitude'] || changes['longitude']) this.applyInputLocation(); }
    ngOnDestroy(): void {
        this.destroyed = true;
        this.abortController.abort();
        this.resizeObserver?.disconnect();
        this.map?.getViewport().removeEventListener('pointerleave', this.onMouseLeave);
        this.map?.setTarget(undefined);
        this.map?.dispose();
        this.map = undefined;
        this.countriesSource?.clear();
        this.provincesSource.clear();
        this.districtsSource.clear();
        this.hoverSource.clear();
        this.selectedSource.clear();
        this.markerSource.clear();
    }

    zoomIn(): void { const view = this.map?.getView(); if (view) view.animate({ zoom: Math.min((view.getZoom() ?? 2) + 1, 14), duration: 180 }); }
    zoomOut(): void { const view = this.map?.getView(); if (view) view.animate({ zoom: Math.max((view.getZoom() ?? 2) - 1, 1), duration: 180 }); }
    resetView(): void { this.map?.getView().animate({ center: fromLonLat([10, 25]), zoom: 2, duration: 250 }); }

    private initializeMap(): void {
        this.countriesSource = new VectorSource<MapRegion>({
            features: new GeoJSON().readFeatures(WORLD_COUNTRIES, { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' }),
            wrapX: false
        });

        const countriesLayer = new VectorLayer({ source: this.countriesSource, style: this.normalStyle });
        this.provinceLayer = new VectorLayer({ source: this.provincesSource, style: this.provinceStyle, visible: false });
        this.districtLayer = new VectorLayer({ source: this.districtsSource, style: this.districtStyle, visible: false });
        const hoverLayer = new VectorLayer({ source: this.hoverSource, style: feature => this.getHighlightStyle(feature, false) });
        const selectedLayer = new VectorLayer({ source: this.selectedSource, style: feature => this.getHighlightStyle(feature, true) });
        const markerLayer = new VectorLayer({ source: this.markerSource, style: new Style({ image: new CircleStyle({ radius: 8, fill: new Fill({ color: '#c73227' }), stroke: new Stroke({ color: '#ffffff', width: 3 }) }) }) });

        this.map = new OLMap({
            target: this.mapElement.nativeElement,
            controls: [],
            layers: [countriesLayer, this.provinceLayer, this.districtLayer, hoverLayer, selectedLayer, markerLayer],
            view: new View({ center: fromLonLat([10, 25]), zoom: 2, minZoom: 1, maxZoom: 14 })
        });

        this.map.on('pointermove', event => {
            if (this.destroyed || event.dragging) return;
            const [longitude, latitude] = toLonLat(event.coordinate);
            const region = this.findVisibleRegion(longitude, latitude);
            const hovered = region === this.selectedRegion ? null : region;
            if (hovered === this.hoveredRegion) return;
            this.hoveredRegion = hovered;
            this.renderHighlight(this.hoverSource, hovered, event.coordinate);
        });
        this.map.getViewport().addEventListener('pointerleave', this.onMouseLeave);

        this.map.on('singleclick', event => {
            if (this.disabled) return;
            const [rawLongitude, latitude] = toLonLat(event.coordinate);
            if (!Number.isFinite(rawLongitude) || !Number.isFinite(latitude) || latitude < -85.05112878 || latitude > 85.05112878) return;
            const location: MapLocation = { Latitude: Number(latitude.toFixed(6)), Longitude: Number(this.normalizeLongitude(rawLongitude).toFixed(6)) };
            this.zone.run(() => { if (this.destroyed) return; this.setSelection(location); this.locationSelected.emit({ ...location }); this.cdr.markForCheck(); });
        });

        this.map.getView().on('change:resolution', () => this.refreshLevel());
        this.resizeObserver = new ResizeObserver(() => { if (!this.destroyed) this.map?.updateSize(); });
        this.resizeObserver.observe(this.mapElement.nativeElement);
        this.applyInputLocation();
        this.refreshLevel();
    }

    private normalizeLongitude(value: number): number { return ((value + 180) % 360 + 360) % 360 - 180; }

    // The data files are deployed with Angular; no third-party HTTP calls occur while the map is running.
    private async loadLocalAreas(filename: string, source: VectorSource<MapRegion>): Promise<void> {
        const url = `${this.mapDataUrl.replace(/\/$/, '')}/${filename}`;
        const response = await fetch(url, { signal: this.abortController.signal });
        if (!response.ok) throw new Error(`${url}: HTTP ${response.status}`);
        const geojson: unknown = await response.json();
        const features = new GeoJSON().readFeatures(geojson, { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' });
        if (features.length === 0) throw new Error(`${url}: boş GeoJSON`);
        if (this.destroyed) return;
        source.addFeatures(features);
        this.refreshLevel();
    }

    private ensureLayersLoaded(zoom: number): void {
        if (zoom >= 5 && !this.provinceLoading) {
            this.provinceLoading = this.loadLocalAreas('tr-provinces.geojson', this.provincesSource).catch(error => {
                if (!this.destroyed) console.error('[LocationMap] İl sınırları yüklenemedi. assets/maps dosyalarını kontrol edin.', error);
            });
        }
        if (zoom >= 8 && !this.districtLoading) {
            this.districtLoading = this.loadLocalAreas('tr-districts.geojson', this.districtsSource).catch(error => {
                if (!this.destroyed) console.error('[LocationMap] İlçe sınırları yüklenemedi. assets/maps dosyalarını kontrol edin.', error);
            });
        }
    }

    private refreshLevel(): void {
        if (!this.map || this.destroyed) return;
        const zoom = this.map.getView().getZoom() ?? 2;
        this.provinceLayer?.setVisible(zoom >= 5);
        this.districtLayer?.setVisible(zoom >= 8);
        this.ensureLayersLoaded(zoom);
        const nextLevel: MapLevel = zoom >= 8 && !this.districtsSource.isEmpty() ? 'district'
            : zoom >= 5 && !this.provincesSource.isEmpty() ? 'province' : 'country';
        if (this.activeLevel === nextLevel) return;
        this.activeLevel = nextLevel;
        this.hoveredRegion = null;
        this.hoverSource.clear();
        this.updateSelectedHighlight();
    }

    private findRegion(source: VectorSource<MapRegion>, longitude: number, latitude: number, maxDistanceMeters = 0): MapRegion | null {
        if (!Number.isFinite(longitude) || !Number.isFinite(latitude) || Math.abs(latitude) > 85.05112878 || source.isEmpty()) return null;
        const coordinate = fromLonLat([this.normalizeLongitude(longitude), latitude]);
        const exact = source.getFeaturesAtCoordinate(coordinate)[0] ?? null;
        if (exact || maxDistanceMeters <= 0) return exact;
        const nearest = source.getClosestFeatureToCoordinate(coordinate);
        const closestPoint = nearest?.getGeometry()?.getClosestPoint(coordinate);
        return nearest && closestPoint && getDistance(toLonLat(coordinate), toLonLat(closestPoint)) <= maxDistanceMeters ? nearest : null;
    }

    // The most detailed boundary available at the current zoom level wins.
    private findVisibleRegion(longitude: number, latitude: number, countryToleranceMeters = 20000): MapRegion | null {
        if (this.activeLevel === 'district') {
            const district = this.findRegion(this.districtsSource, longitude, latitude, 1200);
            if (district) return district;
        }
        if (this.activeLevel !== 'country') {
            const province = this.findRegion(this.provincesSource, longitude, latitude, 3000);
            if (province) return province;
        }
        return this.countriesSource ? this.findRegion(this.countriesSource, longitude, latitude, countryToleranceMeters) : null;
    }

    // Border and label are intentionally separate to avoid multiple labels for a MultiPolygon.
    private renderHighlight(source: VectorSource<MapRegion>, region: MapRegion | null, coordinate: number[]): void {
        source.clear();
        const geometry = region?.getGeometry();
        if (!region || !geometry) return;
        const name = String(region.get('name') ?? region.get('ADM2_TR') ?? region.get('ADM1_TR') ?? '');
        source.addFeatures([
            new Feature({ geometry: geometry.clone(), mapPart: 'region-border' }),
            new Feature({ geometry: new Point(coordinate), mapPart: 'region-label', name })
        ]);
    }

    private getHighlightStyle(feature: FeatureLike, selected: boolean): Style | Style[] {
        if (feature.get('mapPart') === 'region-border') return selected ? this.selectedStyles : this.hoverStyles;
        return new Style({
            text: new Text({
                text: String(feature.get('name') ?? ''),
                font: '700 13px Arial, sans-serif',
                placement: 'point',
                overflow: true,
                offsetY: -26,
                fill: new Fill({ color: '#ffffff' }),
                backgroundFill: new Fill({ color: selected ? '#c73227' : '#17324d' }),
                backgroundStroke: new Stroke({ color: '#ffffff', width: 1.5 }),
                padding: [6, 10, 6, 10]
            })
        });
    }

    private applyInputLocation(): void {
        if (typeof this.latitude !== 'number' || typeof this.longitude !== 'number' || !Number.isFinite(this.latitude) || !Number.isFinite(this.longitude) || Math.abs(this.latitude) > 85.05112878 || Math.abs(this.longitude) > 180) {
            this.setSelection(null);
            return;
        }
        this.setSelection({ Latitude: this.latitude, Longitude: this.longitude });
        this.map?.getView().setCenter(fromLonLat([this.longitude, this.latitude]));
    }

    private updateSelectedHighlight(): void {
        const location = this.selectedLocation;
        this.selectedRegion = location ? this.findVisibleRegion(location.Longitude, location.Latitude, 50000) : null;
        this.renderHighlight(this.selectedSource, this.selectedRegion, location ? fromLonLat([location.Longitude, location.Latitude]) : [0, 0]);
        if (this.hoveredRegion === this.selectedRegion) { this.hoveredRegion = null; this.hoverSource.clear(); }
    }

    private setSelection(location: MapLocation | null): void {
        this.selectedLocation = location;
        this.markerSource.clear();
        if (location) this.markerSource.addFeature(new Feature({ geometry: new Point(fromLonLat([location.Longitude, location.Latitude])) }));
        this.updateSelectedHighlight();
        this.cdr.markForCheck();
    }
}
